import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import CompetenceProfileLayout from './CompetenceProfileLayout';

// import utility file
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { ExclamationTriangleFill } from 'react-bootstrap-icons';
import CompetenceService from '../../services/CompetenceService';

const ViewCompetence = () => {
	const { id } = useParams();
	const [competence, setCompetence] = useState(null);
	const [validated, setValidated] = useState(false);
	const [feedbackMessages, setFeedbackMessages] = useState({});
	const [showAlert, setShowAlert] = useState(false);
	const [alertMessage, setAlertMessage] = useState('');
	const [selectedSection, setSelectedSection] = useState('');
	const [selectedNiveau, setSelectedNiveau] = useState('');

	const SECTION_CHOICES = [
		{ value: 'en', label: 'Angolophone' },
		{ value: 'fr', label: 'Francophone' }
	];

	const NIVEAU_CHOICES = [
		{ value: 1, label: '1' },
		{ value: 2, label: '2' }
	];

	useEffect(() => {
		Promise.all([CompetenceService.getCompetence(id)]).then(
			([competenceData]) => {
				setCompetence(competenceData.data);

				// Set default selection for section and niveau
				if (competenceData && competenceData.data) {
					const competenceSection = competenceData.data.section;
					const competenceNiveau = competenceData.data.niveau;
					setSelectedSection(competenceSection);
					setSelectedNiveau(competenceNiveau);
				}
			}
		);
	}, [id]);

	const handleSubmit = async (event) => {
		event.preventDefault();
		const form = event.currentTarget;

		if (form.checkValidity() === false) {
			event.stopPropagation();
		} else {
			try {
				const competenceData = new FormData();

				// Add all your form data to the FormData
				competenceData.append('nom', form.nom.value);
				competenceData.append('section', form.section.value);
				competenceData.append('niveau', form.niveau.value);
				competenceData.append('points_ecrit', form.points_ecrit.value);
				competenceData.append('points_oral', form.points_oral.value);
				competenceData.append('points_pratique', form.points_pratique.value);
				competenceData.append(
					'points_savoir_etre',
					form.points_savoir_etre.value
				);
				competenceData.append('annee_scolaire', competence.annee_scolaire);

				// Call the AnneescolaireService to create the year
				const response = await CompetenceService.updateCompetence(
					id,
					competenceData
				);

				// Reset the form and state variables after successful update
				form.reset();
				setValidated(false);
				setFeedbackMessages({});

				// Add this line to refresh the page
				window.location.reload();
			} catch (error) {
				console.error(
					'Erreur lors de la mise à jour des informations de la compétence',
					error
				);

				if (error.response && error.response.data) {
					const apiErrors = error.response.data;

					// If there are field-specific errors
					if (typeof apiErrors === 'object' && !Array.isArray(apiErrors)) {
						const newFeedbackMessages = {};

						for (const field in apiErrors) {
							newFeedbackMessages[field] = apiErrors[field][0];
						}

						setFeedbackMessages(newFeedbackMessages);
					}

					// If there's a general error detail message
					if (apiErrors.detail) {
						setAlertMessage(apiErrors.detail[0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					} else if (
						apiErrors['Il ya erreur'] &&
						apiErrors['Il ya erreur'][0]
					) {
						setAlertMessage(apiErrors['Il ya erreur'][0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					}
				} else {
					alert(
						"Une erreur s'est produite lors de la soumission du formulaire."
					);
				}
			}
		}

		setValidated(true);
	};

	return (
		<CompetenceProfileLayout>
			{competence && (
				<>
					<Card className="mb-3 ">
						{/* Card body */}
						<Card.Body>
							{/*  Form  */}
							<Form
								className="mb-3"
								noValidate
								validated={validated}
								onSubmit={handleSubmit}
							>
								{showAlert && (
									<Alert variant="danger">
										<ExclamationTriangleFill size={25} className="me-1" />
										{alertMessage}
									</Alert>
								)}

								<Row>
									{/* Nom de la compétence */}
									<Col xs={12} className="mb-3">
										<Form.Label htmlFor="nom">
											Intitulé <span className="text-danger">*</span>
										</Form.Label>
										<Form.Control
											name="nom"
											type="text"
											placeholder="Saisir le nom de la compétence"
											id="nom"
											required
											isInvalid={!!feedbackMessages['nom']}
											onChange={(e) => {
												setFeedbackMessages((prevMessages) => ({
													...prevMessages,
													nom: e.target.value
														? ''
														: 'Le nom de la compétence est requis.'
												}));
											}}
											defaultValue={competence.nom || ''}
										/>
										<Form.Control.Feedback type="invalid">
											{feedbackMessages['nom']}
										</Form.Control.Feedback>
									</Col>

									<Col xs={12} className="mb-3">
										{/* Section et Niveau  */}
										<Form.Group>
											<Row>
												<Col md={6} sm={12} className="mb-3">
													<Form.Label htmlFor="section">
														Section <span className="text-danger">*</span>
													</Form.Label>
													<Form.Select
														name="section"
														required
														value={selectedSection}
														isInvalid={!!feedbackMessages['section']}
														onChange={(e) => {
															const value = e.target.value;
															setSelectedSection(value);
														}}
													>
														<option value="">Selectionner la section</option>
														{SECTION_CHOICES.map((choice, index) => (
															<option key={index} value={choice.value}>
																{choice.label}
															</option>
														))}
													</Form.Select>
													<Form.Control.Feedback type="invalid">
														{feedbackMessages['section']}
													</Form.Control.Feedback>
												</Col>

												<Col md={6} sm={12} className="mb-3">
													<Form.Label htmlFor="niveau">
														Niveau <span className="text-danger">*</span>
													</Form.Label>
													<Form.Select
														name="niveau"
														required
														value={selectedNiveau}
														isInvalid={!!feedbackMessages['niveau']}
														onChange={(e) => {
															const value = e.target.value;
															setSelectedNiveau(value);
														}}
													>
														<option value="">Selectionner le niveau</option>
														{NIVEAU_CHOICES.map((choice, index) => (
															<option key={index} value={choice.value}>
																{choice.label}
															</option>
														))}
													</Form.Select>
													<Form.Control.Feedback type="invalid">
														{feedbackMessages['niveau']}
													</Form.Control.Feedback>
												</Col>
											</Row>
										</Form.Group>
									</Col>

									{/* Points */}
									<Col xs={12} className="mb-3">
										{/* Points ecrit et oral  */}
										<Form.Group>
											<Row>
												<Col md={6} sm={12} className="mb-3">
													<Form.Group className="mb-3">
														<Form.Label htmlFor="points_ecrit">
															Point écrit
														</Form.Label>
														<Form.Control
															name="points_ecrit"
															type="number"
															min="0"
															placeholder="... points"
															id="points_ecrit"
															isInvalid={!!feedbackMessages['points_ecrit']}
															onChange={(e) => {
																setFeedbackMessages((prevMessages) => ({
																	...prevMessages,
																	points_ecrit: e.target.value
																		? ''
																		: "La valeur sasie n'est pas un nombre."
																}));
															}}
															defaultValue={competence.points_ecrit || ''}
														/>
														<Form.Control.Feedback type="invalid">
															{feedbackMessages['points_ecrit']}
														</Form.Control.Feedback>
													</Form.Group>
												</Col>

												<Col md={6} sm={12} className="mb-3">
													<Form.Group className="mb-3">
														<Form.Label htmlFor="points_oral">
															Points oral
														</Form.Label>
														<Form.Control
															name="points_oral"
															type="number"
															min="0"
															placeholder="... points"
															id="points_oral"
															isInvalid={!!feedbackMessages['points_oral']}
															onChange={(e) => {
																setFeedbackMessages((prevMessages) => ({
																	...prevMessages,
																	points_oral: e.target.value
																		? ''
																		: "La valeur sasie n'est pas un nombre."
																}));
															}}
															defaultValue={competence.points_oral || ''}
														/>
														<Form.Control.Feedback type="invalid">
															{feedbackMessages['points_oral']}
														</Form.Control.Feedback>
													</Form.Group>
												</Col>
											</Row>
										</Form.Group>
									</Col>

									<Col xs={12} className="mb-3">
										{/* Points pratique et savoir être  */}
										<Form.Group>
											<Row>
												<Col md={6} sm={12} className="mb-3">
													<Form.Group className="mb-3">
														<Form.Label htmlFor="points_pratique">
															Point pratique
														</Form.Label>
														<Form.Control
															name="points_pratique"
															type="number"
															min="0"
															placeholder="... points"
															id="points_pratique"
															isInvalid={!!feedbackMessages['points_pratique']}
															onChange={(e) => {
																setFeedbackMessages((prevMessages) => ({
																	...prevMessages,
																	points_pratique: e.target.value
																		? ''
																		: "La valeur sasie n'est pas un nombre."
																}));
															}}
															defaultValue={competence.points_pratique || ''}
														/>
														<Form.Control.Feedback type="invalid">
															{feedbackMessages['points_pratique']}
														</Form.Control.Feedback>
													</Form.Group>
												</Col>

												<Col md={6} sm={12} className="mb-3">
													<Form.Group className="mb-3">
														<Form.Label htmlFor="points_savoir_etre">
															Points savoir-être
														</Form.Label>
														<Form.Control
															name="points_savoir_etre"
															type="number"
															min="0"
															placeholder="... points"
															id="points_savoir_etre"
															isInvalid={
																!!feedbackMessages['points_savoir_etre']
															}
															onChange={(e) => {
																setFeedbackMessages((prevMessages) => ({
																	...prevMessages,
																	points_savoir_etre: e.target.value
																		? ''
																		: "La valeur sasie n'est pas un nombre."
																}));
															}}
															defaultValue={competence.points_savoir_etre || ''}
														/>
														<Form.Control.Feedback type="invalid">
															{feedbackMessages['points_savoir_etre']}
														</Form.Control.Feedback>
													</Form.Group>
												</Col>
											</Row>
										</Form.Group>
									</Col>

									{/* Buttons */}
									<Col xs={12} className="mt-4">
										<Button variant="success" type="submit">
											Mettre à jour
										</Button>
										<Button
											type="reset"
											variant="outline-danger"
											className="ms-2"
										>
											Effacer
										</Button>
									</Col>
								</Row>
							</Form>
						</Card.Body>
					</Card>
				</>
			)}
		</CompetenceProfileLayout>
	);
};

export default ViewCompetence;
